import { Link, useParams } from "react-router-dom";
import { useGetSingleDeviceAttribute } from "../../../../api/routes/attributes";
import { DataTable } from "./data-table";
import { columns } from "./columns";
import { Button } from "../../../../components/ui/button";
import { useEffect, useRef } from "react";

const SingleDeviceAttributeRoute: React.FC = () => {
    const { deviceId, attributeId } = useParams();

    const {
        data: attributeData,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        refetch: refetchAttributeData,
    } = useGetSingleDeviceAttribute(deviceId as string, attributeId as string);

    const loadMoreRef = useRef<HTMLDivElement>(null);

    // IntersectionObserver to load more when the user scrolls to the bottom
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage();
                }
            },
            {
                rootMargin: "100px",
            }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => {
            if (loadMoreRef.current) {
                observer.unobserve(loadMoreRef.current);
            }
        };
    }, [hasNextPage, fetchNextPage]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (!attributeData) {
        return <div>Attribute not found</div>;
    }

    return (
        <div className="container mx-auto py-10">
            <div className="row">
                <h1 className="text-2xl font-bold mb-4"></h1>

                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">
                        {attributeData.pages[0].attribute.attributeName} -{" "}
                        {attributeData.pages[0].attribute.machineName}
                    </h2>
                    <Button variant="secondary" size="sm" onClick={() => refetchAttributeData()}>
                        Refresh
                    </Button>
                </div>
                <div className="flex gap-2 mb-4">
                    <Link to="/devices" className="text-blue-600 hover:underline">
                        Devices
                    </Link>
                    <span className="text-blue-600">/</span>
                    <Link
                        to={`/devices/${deviceId}/live`}
                        className="text-blue-600 hover:underline"
                    >
                        {attributeData.pages[0].attribute.machineName}
                    </Link>
                    <span className="text-blue-600">/</span>
                    <span>{attributeData.pages[0].attribute.attributeName}</span>
                </div>
            </div>

            <DataTable columns={columns} data={attributeData.pages.flatMap((page) => page.data)} />

            {/* Loading indicator for fetching next page */}
            {isFetchingNextPage && <div className="text-center pt-4">Loading more...</div>}

            {/* Sentinel element to trigger the next page loading */}
            <div ref={loadMoreRef} style={{ height: "20px" }} />
        </div>
    );
};

export { SingleDeviceAttributeRoute };
